import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

const LIGHT_COLORS = ["#FFFFFF", "#F2C230"];

// "Maa Shailaputri" -> "Shailaputri", "Ghatasthapana · Maa Shailaputri" -> "Shailaputri".
const deviName = (name = "") => name.split(" · ").pop().replace(/^Maa /, "");

// Day-by-day view of the occurrence's child observances. The parent theme
// stays fixed; only the highlighted day moves (PRD §5 "critical rule").
// Ten names never fit under ten dots, so instead of truncating every label
// the caption below the row names one day: today by default, or whichever
// dot is hovered / focused / tapped.
const ObservanceDayModule = ({ experience }) => {
  const { days = [] } = experience;
  const listRef = useRef(null);
  const [pickedKey, setPickedKey] = useState(null);

  // On narrow screens the row scrolls; keep today in view.
  useEffect(() => {
    const list = listRef.current;
    const today = list?.querySelector('[aria-current="date"]');
    if (!list || !today || list.scrollWidth <= list.clientWidth) return;
    list.scrollLeft = today.offsetLeft - list.clientWidth / 2 + today.clientWidth / 2;
  }, [experience.activeDay?.key]);

  if (!days.length) return null;

  const fallback = days.find((d) => d.state === "today") || days.find((d) => d.state !== "past") || days[days.length - 1];
  const shown = days.find((d) => d.key === pickedKey) || fallback;

  return (
    <div>
      <ol
        ref={listRef}
        className="relative flex items-start justify-between gap-1 overflow-x-auto scrollbar-hide pt-4 pb-1"
        aria-label={`${experience.primaryCampaign?.festivalName || "Festival"} days`}
        onMouseLeave={() => setPickedKey(null)}
      >
        {days.map((d, i) => {
          const today = d.state === "today";
          const past = d.state === "past";
          const color = d.color || "#C8942E";
          return (
            <li
              key={d.key}
              tabIndex={0}
              title={`${d.label} · ${d.name} · ${d.shortDate}`}
              className="group relative flex w-[46px] shrink-0 lg:w-auto lg:min-w-0 lg:flex-1 cursor-default flex-col items-center gap-1.5 text-center outline-none"
              style={{ "--dc": color }}
              aria-current={today ? "date" : undefined}
              onMouseEnter={() => setPickedKey(d.key)}
              onFocus={() => setPickedKey(d.key)}
              onBlur={() => setPickedKey(null)}
              onClick={() => setPickedKey(d.key)}
            >
              <div className="relative flex h-12 w-full items-center justify-center">
                {i < days.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-1/2 top-1/2 h-[2px] w-[calc(100%+4px)] -translate-y-1/2 ${past ? "bg-[hsl(var(--fx-accent)/0.7)]" : "bg-white/15"}`}
                  />
                )}
                {today && (
                  <>
                    <span aria-hidden="true" className="fxd-ring absolute h-12 w-12 rounded-full" />
                    <span aria-hidden="true" className="fxd-ring fxd-ring-2 absolute h-12 w-12 rounded-full" />
                  </>
                )}
                <span
                  className={`relative z-10 flex items-center justify-center rounded-full border-2 font-bold transition-transform duration-200 group-hover:scale-110 group-focus-visible:scale-110 ${
                    today
                      ? "fxd-today h-12 w-12 border-[hsl(var(--fx-accent))] text-base"
                      : "h-9 w-9 border-white/25 text-xs"
                  } ${past ? "brightness-[.6] saturate-[.55] group-hover:brightness-100 group-hover:saturate-100" : ""} ${shown.key === d.key && !today ? "ring-2 ring-[hsl(var(--fx-accent))] ring-offset-2 ring-offset-transparent" : ""}`}
                  style={{ backgroundColor: color, color: LIGHT_COLORS.includes(color.toUpperCase()) ? "#4a1016" : "#fff" }}
                >
                  {past ? <Check className="h-4 w-4" /> : i + 1}
                </span>
              </div>
              {today && (
                <span className="fxd-chip rounded-full bg-[hsl(var(--fx-accent))] px-2 py-[1px] font-sans text-[9px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--fx-accent-fg))]">
                  Today
                </span>
              )}
            </li>
          );
        })}
      </ol>

      <p className="mt-2 min-h-[20px] text-center font-sans text-xs text-[hsl(var(--fx-muted))]" aria-live="polite">
        <span key={shown.key} className="fxd-caption inline-block">
          <span className="font-semibold text-[hsl(var(--fx-fg))]">{shown.label}</span>
          {" · "}
          <span className={shown.state === "today" ? "font-bold text-[hsl(var(--fx-accent))]" : "font-semibold text-[hsl(var(--fx-fg))]"}>{deviName(shown.name)}</span>
          {" · "}
          {shown.shortDate}
          {shown.colorName ? ` · ${shown.colorName}` : ""}
        </span>
      </p>

      <style>{`
        .fxd-today{box-shadow:0 0 22px color-mix(in srgb,var(--dc) 75%,transparent);animation:fxd-breathe 2.4s ease-in-out infinite}
        .fxd-ring{border:2px solid var(--dc);animation:fxd-ripple 2.4s cubic-bezier(0,0,.2,1) infinite}
        .fxd-ring-2{animation-delay:1.2s}
        .fxd-chip{animation:fxd-pop .45s cubic-bezier(.34,1.56,.64,1) both}
        .fxd-caption{animation:fxd-fade .25s ease-out both}
        @keyframes fxd-breathe{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}
        @keyframes fxd-ripple{0%{transform:scale(1);opacity:.75}100%{transform:scale(1.7);opacity:0}}
        @keyframes fxd-pop{0%{transform:scale(.6);opacity:0}100%{transform:scale(1);opacity:1}}
        @keyframes fxd-fade{0%{transform:translateY(3px);opacity:0}100%{transform:none;opacity:1}}
        @media (prefers-reduced-motion:reduce){
          .fxd-today,.fxd-chip,.fxd-caption{animation:none}
          .fxd-ring{display:none}
        }
      `}</style>
    </div>
  );
};

export default ObservanceDayModule;
